import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import makeStyles from '@mui/styles/makeStyles';
import Grid from '@mui/material/Grid';
import { grey } from '@mui/material/colors';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import Card from '@mui/material/Card';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import pickBy from 'lodash/pickBy';

import { CollapseCard, Title, RatingScale, Loader } from 'ui';
import { PyramidChart } from 'ui/Charts';
import { SummaryCardDocuments } from 'modules/common/SummaryCard';
import {
  summaryCardTablevels,
  datesLabels as defaultDatesLabels,
  generalInformationLabels,
  additionalInformationLabels,
} from 'shared/constants/summaryTab';
import { summaryCardLevelsMapping } from 'shared/mappings';

const useStyles = makeStyles((theme) => ({
  head: {
    '& > .MuiGrid-item': {
      padding: `0 ${theme.spacing(2)}`,
      margin: `${theme.spacing(2)} 0`,
      borderRight: `2px solid ${grey[200]}`,
    },
    '& > .MuiGrid-item:last-child, & > .MuiGrid-item:first-child': {
      border: 'none',
    },
  },
  headSquare: {
    '&.MuiGrid-item': {
      padding: 0,
      margin: 0,
    },
  },
  content: {
    '& > .MuiGrid-item': {
      padding: `0 ${theme.spacing(2)}`,
      margin: `${theme.spacing(2)} 0`,
      borderRight: `2px solid ${grey[200]}`,
    },
    '& > .MuiGrid-item:last-child': {
      border: 'none',
    },
  },
  latestChange: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    textAlign: 'center',
  },
  dataQuality: {
    flexDirection: 'column',
    gridGap: theme.spacing(4),
    height: '100%',
    textAlign: 'center',
  },
  dataQualityDisplay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gridGap: theme.spacing(1),
  },
  generalInformationList: {
    padding: 0,
    '& li': {
      padding: 0,
    },
  },
  generalInformationListIcon: {
    color: theme.palette.primary.main,
  },
  level: {
    fontWeight: 500,
    display: 'flex',
    fontSize: 20,
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
    borderBottom: `1px dashed ${grey[400]}`,
  },
  infoData: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& > div:nth-child(2)': {
      fontWeight: 500,
    },
  },
  titleMarginBottom: {
    marginBottom: theme.spacing(2),
  },
  titleMarginTopBottom: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));
const useListItemIconStyles = makeStyles({
  root: {
    minWidth: 35,
  },
});

const SummaryCard = ({
  isOpen = false,
  isLoading,
  className,
  headSquare,
  main = undefined,
  dataQuality,
  history,
  data,
  documentHandling = undefined,
  onlyHeader = false,
  ...props
}) => {
  const classes = useStyles();
  const listItemIconClasses = useListItemIconStyles();
  const { t } = useTranslation();

  if (isLoading) {
    return <Loader center />;
  }

  const {
    latestChange,
    generalInformation,
    generalInformationList,
    additionalInformation: initialAdditionalInformation,
    endorsementList,
    levels,
    dates,
    datesLabels = defaultDatesLabels,
    documents,
    ratingScalesData,
  } = data;

  const level = summaryCardLevelsMapping[levels.level];
  const levelToDisplay = level + 1;

  const additionalInformation = pickBy(initialAdditionalInformation);

  const head = (
    <Grid container className={classes.head}>
      <Grid item xs={2} className={classes.headSquare}>
        {headSquare}
      </Grid>
      <Grid item xs={3}>
        <Title>{t('History')}</Title>
        <Grid container justifyContent="center">
          <Grid item>{history}</Grid>
        </Grid>
      </Grid>
      {dataQuality ? (
        <Grid item xs={3}>
          <Grid container className={classes.dataQuality}>
            <Grid item>
              <Title align="center">{t('Data quality')}</Title>
            </Grid>
            <Grid item className={classes.dataQualityDisplay}>
              <CheckCircleIcon className={classes.generalInformationListIcon} />
              <Typography variant="h5">{dataQuality.quality}</Typography>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid item xs={3}>
          <Grid container className={classes.latestChange}>
            <Grid item>
              <Title align="center">{t('Latest change')}</Title>
            </Grid>
            <Grid item>
              <Typography variant="h5">{latestChange.status || t('New')}</Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle1">{latestChange.date}</Typography>
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid item xs={4}>
        <Title>{t('General information')}</Title>
        <List className={classes.generalInformationList}>
          {generalInformationList.map((element) => (
            <ListItem key={element} disableGutters>
              <ListItemIcon classes={listItemIconClasses}>
                <CheckCircleIcon className={classes.generalInformationListIcon} />
              </ListItemIcon>
              <ListItemText>{element}</ListItemText>
            </ListItem>
          ))}
        </List>
      </Grid>
    </Grid>
  );

  if (onlyHeader) {
    return <Card className={className}>{head}</Card>;
  }

  return (
    <CollapseCard head={head} isOpen={isOpen} className={className} {...props}>
      {main}
      <Grid container className={classes.content}>
        <Grid item xs={5}>
          <Title
            tooltip="The graph describes the composition of Scope's analytical value chain."
            bottomLine
          >
            {t('Analytical depth')}
          </Title>
          <Grid container className={classes.level}>
            <Grid item>
              {t('Level')} {levelToDisplay} ({levels.level})
            </Grid>
            <Grid item>{levels.title}</Grid>
          </Grid>
          <PyramidChart data={summaryCardTablevels} highlight={level} />
        </Grid>
        <Grid item xs={4}>
          {dates && (
            <>
              <Title bottomLine className={classes.titleMarginBottom}>
                {t('Dates')}
              </Title>
              {Object.keys(dates).map((key) => (
                <Grid container key={key} className={classes.infoData}>
                  <Grid item>{datesLabels[key]}</Grid>
                  <Grid item>{dates[key] || t('Not available')}</Grid>
                </Grid>
              ))}
            </>
          )}
          {endorsementList && endorsementList.length ? (
            <>
              <Title bottomLine className={classes.titleMarginTopBottom}>
                {t('Endorsements')}
              </Title>
              {endorsementList.map(({ label, value }) => (
                <Grid container key={value} className={classes.infoData}>
                  <Grid item>{label}</Grid>
                  <Grid item>{value}</Grid>
                </Grid>
              ))}
            </>
          ) : null}
          {!isEmpty(generalInformation) ? (
            <>
              <Title bottomLine className={classes.titleMarginTopBottom}>
                {t('General Information')}
              </Title>
              {Object.keys(generalInformation).map((key) => (
                <Grid container key={key} className={classes.infoData}>
                  <Grid item>{generalInformationLabels[key]}</Grid>
                  <Grid item>{generalInformation[key]}</Grid>
                </Grid>
              ))}
            </>
          ) : null}
          {!isEmpty(additionalInformation) ? (
            <>
              <Title bottomLine className={classes.titleMarginTopBottom}>
                {t('Additional Information')}
              </Title>
              {Object.keys(additionalInformation).map((key) => (
                <Grid container key={key} className={classes.infoData}>
                  <Grid item>{additionalInformationLabels[key]}</Grid>
                  <Grid item>{additionalInformation[key]}</Grid>
                </Grid>
              ))}
            </>
          ) : null}
          <Title bottomLine className={classes.titleMarginTopBottom}>
            {t('Documents')}
          </Title>
          <SummaryCardDocuments documentTypes={documents} documentHandling={documentHandling} />
        </Grid>
        <Grid item xs={3}>
          <Title bottomLine className={classes.titleMarginBottom}>
            {t('Applicable scale')}
          </Title>
          <Grid container justifyContent="center">
            <Grid item xs={5}>
              <RatingScale data={ratingScalesData} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </CollapseCard>
  );
};

SummaryCard.defaultProps = {
  isOpen: false,
  isLoading: false,
  className: undefined,
  main: undefined,
  history: undefined,
  documentHandling: undefined,
  dataQuality: undefined,
};

SummaryCard.propTypes = {
  headSquare: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  main: PropTypes.node,
  history: PropTypes.node,
  documentHandling: PropTypes.object,
  dataQuality: PropTypes.shape({
    quality: PropTypes.string,
  }),
  data: PropTypes.shape({
    latestChange: PropTypes.shape({
      date: PropTypes.string,
      status: PropTypes.string,
    }),
    generalInformationList: PropTypes.arrayOf(PropTypes.string),
    endorsementList: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      }),
    ),
    levels: PropTypes.shape({
      level: PropTypes.string,
      title: PropTypes.string,
    }),
    dates: PropTypes.object,
    datesLabels: PropTypes.object,
    generalInformation: PropTypes.object,
    additionalInformation: PropTypes.object,
    documents: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        path: PropTypes.string,
        publicationDate: PropTypes.string,
        title: PropTypes.string,
        options: PropTypes.object,
        retries: PropTypes.number,
      }),
    ),
    ratingScalesData: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        tooltip: PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default SummaryCard;
